import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact "correct the lesson budget" modal.
 *
 * Lets the instructor set the exact desired numerator ("gereden") and
 * denominator ("totaal gekocht") in one shot. The backend then:
 *   - deletes stray registered lessons (default on)
 *   - sets initial_lessons = target_driven
 *   - sets extra_lessons  = target_total - target_driven
 *   - clears package_type + free_lessons
 *
 * Result: the leskaart shows exactly target_driven / target_total afterwards.
 */
export default function LessonBudgetFixModal({ open, onClose, studentId, studentName, initialDriven, initialTotal, onSaved }) {
    const [driven, setDriven] = useState(initialDriven ?? 0);
    const [total, setTotal] = useState(initialTotal ?? 0);
    const [deleteHistory, setDeleteHistory] = useState(true);
    const [deleteScheduled, setDeleteScheduled] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Re-sync when opened for a different student.
    const [seed, setSeed] = useState(`${studentId}-${initialDriven}-${initialTotal}`);
    if (open && seed !== `${studentId}-${initialDriven}-${initialTotal}`) {
        setDriven(initialDriven ?? 0);
        setTotal(initialTotal ?? 0);
        setSeed(`${studentId}-${initialDriven}-${initialTotal}`);
        setError('');
    }

    const remaining = Math.max(0, Number(total) - Number(driven));

    const submit = async () => {
        setError('');
        const d = parseFloat(driven);
        const t = parseFloat(total);
        if (Number.isNaN(d) || Number.isNaN(t) || d < 0 || t < 0) {
            setError('Vul geldige, positieve getallen in.');
            return;
        }
        if (d > t) {
            setError('Aantal gereden lessen kan niet hoger zijn dan totaal gekocht.');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/students/${studentId}/fix-budget`, {
                target_driven: d,
                target_total: t,
                delete_lesson_history: !!deleteHistory,
                delete_scheduled_history: !!deleteScheduled,
            }, { withCredentials: true });
            if (onSaved) onSaved({ driven: d, total: t });
            onClose();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Kon lesbudget niet corrigeren.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-md bg-[#2D3748] border-[#4A5568] text-white rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Lesbudget corrigeren
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                        Zet in één keer de juiste waarden voor <span className="text-white font-semibold">{studentName}</span>.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="fix-driven" className="text-gray-300">Aantal reeds gereden lessen</Label>
                        <Input
                            id="fix-driven"
                            type="number"
                            step="0.5"
                            min="0"
                            value={driven}
                            onChange={(e) => setDriven(e.target.value)}
                            className="bg-[#1A202C] border-[#4A5568] text-white"
                            data-testid="fix-budget-driven"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="fix-total" className="text-gray-300">Aantal totaal gekochte lessen</Label>
                        <Input
                            id="fix-total"
                            type="number"
                            step="0.5"
                            min="0"
                            value={total}
                            onChange={(e) => setTotal(e.target.value)}
                            className="bg-[#1A202C] border-[#4A5568] text-white"
                            data-testid="fix-budget-total"
                        />
                    </div>

                    <div className="p-3 rounded-lg bg-[#1A202C] border border-[#4A5568] text-sm text-gray-300">
                        Na opslaan toont de leskaart:{' '}
                        <span className="text-[#E5B84A] font-bold tabular-nums" data-testid="fix-budget-preview">
                            {driven} / {total}
                        </span>{' '}
                        met <span className="text-[#E5B84A] font-bold tabular-nums">{remaining}</span> lessen nog te rijden.
                    </div>

                    <div className="space-y-2">
                        <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={deleteHistory}
                                onChange={(e) => setDeleteHistory(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-[#D4A537]"
                                data-testid="fix-budget-delete-history"
                            />
                            <span>
                                <span className="text-white">Verwijder oude lesregistraties</span>
                                <span className="block text-gray-500 text-xs">Aanbevolen — verwijdert stale <em>Beoordeeld</em> lessen zodat de teller correct blijft.</span>
                            </span>
                        </label>
                        <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={deleteScheduled}
                                onChange={(e) => setDeleteScheduled(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-[#D4A537]"
                                data-testid="fix-budget-delete-scheduled"
                            />
                            <span>
                                <span className="text-white">Verwijder ook oude agenda-lessen</span>
                                <span className="block text-gray-500 text-xs">Optioneel — alle geplande lessen vóór vandaag worden verwijderd.</span>
                            </span>
                        </label>
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 p-2 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-sm">
                            <Warning size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
                            {error}
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} className="border-[#4A5568] text-gray-200 hover:bg-[#3D4A5C]">
                        Annuleren
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={saving}
                        className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                        data-testid="fix-budget-save"
                    >
                        {saving ? 'Opslaan…' : 'Corrigeer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
